import React, { useState, FormEvent, ChangeEvent, useRef, KeyboardEvent } from 'react';
import './App.css';

// Ответ сервера при успешном входе
interface LoginResponse {
  redirect_url: string;
}

// Ответ сервера при ошибке
interface ErrorResponse {
  detail: string;
}

// Иконка «глаз» из макета: наследует currentColor, поэтому реагирует на hover
const EyeGlyph = ({ off }: { off: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1.8 12S5.6 5.2 12 5.2 22.2 12 22.2 12 18.4 18.8 12 18.8 1.8 12 1.8 12Z" />
    <circle cx="12" cy="12" r="3.1" />
    {off && <path d="M4 20 20 4" />}
  </svg>
);

function App() {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Backend читает поля через Form(...) — отправляем как форму, не JSON.
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);

    try {
      // Относительный путь: фронт и API на одном домене, CORS не задействован.
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        const data: LoginResponse = await response.json();
        // Куда идти — решает сервер: у каждого проекта свой путь.
        window.location.replace(data.redirect_url);
        return;
      }

      const errorData: ErrorResponse = await response.json().catch(() => ({ detail: '' }));
      setError(errorData.detail || 'Неверный email или пароль');
    } catch (err) {
      setError('Не удалось связаться с сервером. Попробуйте ещё раз.');
      console.error('Login error:', err);
    }
    setLoading(false);
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
    if (error) setError('');
  };

  // Enter в поле email переводит на пароль, в пароле — отправляет форму
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, field: 'email' | 'password'): void => {
    if (e.key !== 'Enter') return;
    if (field === 'email' && email.trim()) {
      e.preventDefault();
      passwordRef.current?.focus();
    } else if (field === 'password' && password.trim()) {
      const form = e.currentTarget.closest('form');
      if (form) form.requestSubmit();
    }
  };

  return (
    <div className="page">
      <div className="page__glow" aria-hidden="true" />
      <div className="page__stripes" aria-hidden="true" />

      <div className="layout">
        {/* Левая колонка — бренд */}
        <section className="brand">
          <div className="brand__rule" />
          <div className="brand__text">
            <div className="brand__title">KD-systems</div>
            <div className="brand__subtitle">Project management</div>
          </div>
          <div className="brand__rule" />
        </section>

        {/* Правая колонка — форма */}
        <section className="cardWrap">
          <div className="card">
            <header className="card__head">
              <h1 className="card__title">Войти в аккаунт</h1>
              <p className="card__lead">Введите ваши данные для входа в систему</p>
            </header>

            <form onSubmit={handleSubmit} className="form">
              <label className="field">
                <span className="field__label">Email</span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={(e) => handleKeyDown(e, 'email')}
                  className={error ? 'field__input field__input--error' : 'field__input'}
                  placeholder="name@company.ru"
                  autoComplete="username"
                  required
                  autoFocus
                  disabled={loading}
                />
              </label>

              <label className="field">
                <span className="field__label">Пароль</span>
                <span className="field__pwWrap">
                  <input
                    id="password"
                    ref={passwordRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={(e) => handleKeyDown(e, 'password')}
                    className={
                      error
                        ? 'field__input field__input--pw field__input--error'
                        : 'field__input field__input--pw'
                    }
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="pwToggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    tabIndex={-1}
                    disabled={loading}
                  >
                    <EyeGlyph off={!showPassword} />
                  </button>
                </span>
              </label>

              {error && (
                <div className="alert" role="alert">
                  {error}
                </div>
              )}

              <div className="row">
                <label className="check">
                  <input
                    type="checkbox"
                    className="check__input"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="check__box" aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#f3fbf5"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.2 6.3 4.7 8.8 9.8 3.4" />
                    </svg>
                  </span>
                  Запомнить меня
                </label>

                <button
                  type="button"
                  className="linkBtn"
                  onClick={() => setShowHelp(!showHelp)}
                >
                  Забыли пароль?
                </button>
              </div>

              {showHelp && (
                <p className="hint">
                  Восстановление пароля производит администратор вашего проекта — обратитесь к нему.
                </p>
              )}

              <button type="submit" className="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Вход…
                  </>
                ) : (
                  <>
                    Войти в личный кабинет
                    <span className="submit__arrow" aria-hidden="true">→</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      </div>

      <footer className="copyright">© 2026 KD-systems. Все права защищены.</footer>
    </div>
  );
}

export default App;
